/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
// console.log("Hello World! (from ultime-blocks-sql-transaction-visualizer-fr block)");
/* eslint-enable no-console */

(function () {
    // ── Données initiales ──────────────────────────────────
    const comptes = [
        { id: 1, titulaire: "Alice", solde: 3000 },
        { id: 2, titulaire: "Bob", solde: 1500 },
        { id: 3, titulaire: "Claire", solde: 2200 },
        { id: 4, titulaire: "David", solde: 800 }
    ];

    const COLS = ["id", "titulaire", "solde"];

    // ── Helpers ────────────────────────────────────────────
    function tableHtml(data, cols, stateMap) {
        const thead = cols.map(c => `<th>${c}</th>`).join("");
        const rows = data.map(r => {
            const cls = stateMap ? (stateMap[r.id] || "") : "";
            return `<tr class="${cls}">${cols.map(c => `<td>${r[c] !== undefined ? r[c] : "NULL"}</td>`).join("")}</tr>`;
        }).join("");
        return `<table class="mini"><thead><tr>${thead}</tr></thead><tbody>${rows}</tbody></table>`;
    }

    function blockHtml(headerCls, headerLabel, content) {
        return `<div class="table-block">
            <div class="table-block-header ${headerCls}">${headerLabel}</div>
            <div class="table-block-body">${content}</div>
        </div>`;
    }

    function legendHtml(items) {
        if (!items.length) return "";
        const spans = items.map(([color, label]) =>
            `<span><i class="legend-dot" style="background:${color};border:1px solid #555;"></i> ${label}</span>`
        ).join("");
        return `<div class="legend">${spans}</div>`;
    }

    function bannerHtml(type, icon, text) {
        return `<div class="tx-banner ${type}">${icon} ${text}</div>`;
    }

    // ── Définition des 3 scénarios ─────────────────────────
    const queries = {

        // ── COMMIT ────────────────────────────────────────
        commit: {
            parts: [
                { id: "cm-begin", text: "BEGIN;", cls: "commit" },
                { id: "cm-update1", text: "  UPDATE comptes SET solde = solde - 500 WHERE id = 1;", cls: "" },
                { id: "cm-update2", text: "  UPDATE comptes SET solde = solde + 500 WHERE id = 2;", cls: "" },
                { id: "cm-insert", text: "  INSERT INTO comptes VALUES (5, 'Emma', 1000);", cls: "" },
                { id: "cm-commit", text: "COMMIT;", cls: "commit" }
            ],
            steps: [
                {
                    key: "cm-begin",
                    sql: "BEGIN — ouverture de la transaction",
                    desc: "La transaction démarre. Toutes les modifications qui suivent sont <strong>temporaires</strong> jusqu'au COMMIT ou ROLLBACK. La base est encore intacte.",
                    render: () => {
                        return bannerHtml("open", "🔓", "Transaction ouverte — aucune modification encore appliquée") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État actuel de la table comptes", tableHtml(comptes, COLS, null))}
                            </div>`;
                    }
                },
                {
                    key: "cm-update1",
                    sql: "UPDATE — débit du compte d'Alice",
                    desc: "Alice perd 500. La modification est <strong>en mémoire uniquement</strong> (non visible aux autres sessions tant que COMMIT n'est pas fait).",
                    render: () => {
                        const data = comptes.map(r => r.id === 1
                            ? { ...r, solde: r.solde - 500, _state: "modified" }
                            : { ...r, _state: "normal" }
                        );
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state === "modified" ? "modified" : ""; });
                        return bannerHtml("open", "⏳", "Transaction en cours — modifications temporaires") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("pending", "⏳ État en mémoire (non committé)", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#93c5fd", "Ligne modifiée (non encore persistée)"]])}`;
                    }
                },
                {
                    key: "cm-update2",
                    sql: "UPDATE — crédit du compte de Bob",
                    desc: "Bob reçoit 500. Les deux modifications sont en mémoire. Si la session plante ici, <strong>rien ne sera persisté</strong>.",
                    render: () => {
                        const data = comptes.map(r =>
                            r.id === 1 ? { ...r, solde: r.solde - 500, _state: "modified" } :
                                r.id === 2 ? { ...r, solde: r.solde + 500, _state: "modified" } :
                                    { ...r, _state: "normal" }
                        );
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state === "modified" ? "modified" : ""; });
                        return bannerHtml("open", "⏳", "Transaction en cours — 2 modifications temporaires") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("pending", "⏳ État en mémoire (non committé)", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#93c5fd", "Ligne modifiée (non encore persistée)"]])}`;
                    }
                },
                {
                    key: "cm-insert",
                    sql: "INSERT — ajout du compte d'Emma",
                    desc: "Une nouvelle ligne est ajoutée en mémoire. La table a maintenant 3 opérations en attente de validation.",
                    render: () => {
                        const data = [
                            ...comptes.map(r =>
                                r.id === 1 ? { ...r, solde: r.solde - 500, _state: "modified" } :
                                    r.id === 2 ? { ...r, solde: r.solde + 500, _state: "modified" } :
                                        { ...r, _state: "normal" }
                            ),
                            { id: 5, titulaire: "Emma", solde: 1000, _state: "inserted" }
                        ];
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state !== "normal" ? r._state : ""; });
                        return bannerHtml("open", "⏳", "Transaction en cours — 3 opérations en attente") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("pending", "⏳ État en mémoire (non committé)", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#93c5fd", "Modifié"], ["#bbf7d0", "Inséré (non encore persisté)"]])}`;
                    }
                },
                {
                    key: "cm-commit",
                    sql: "COMMIT — validation définitive",
                    desc: "<strong>Toutes les modifications sont persistées</strong> en base de données et deviennent visibles pour toutes les sessions. La transaction est fermée.",
                    render: () => {
                        const final = [
                            ...comptes.map(r =>
                                r.id === 1 ? { ...r, solde: r.solde - 500 } :
                                    r.id === 2 ? { ...r, solde: r.solde + 500 } :
                                        { ...r }
                            ),
                            { id: 5, titulaire: "Emma", solde: 1000 }
                        ];
                        const stateMap = { 1: "committed", 2: "committed", 5: "committed" };
                        return bannerHtml("commit", "✅", "COMMIT — toutes les modifications sont persistées en base") +
                            `<div class="result-block">
                                ${blockHtml("commit", "✅ État final après COMMIT", tableHtml(final, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#86efac", "Ligne validée et persistée définitivement"]])}`;
                    }
                }
            ]
        },

        // ── ROLLBACK ──────────────────────────────────────
        rollback: {
            parts: [
                { id: "rb-begin", text: "BEGIN;", cls: "commit" },
                { id: "rb-update1", text: "  UPDATE comptes SET solde = solde - 500 WHERE id = 1;", cls: "" },
                { id: "rb-update2", text: "  UPDATE comptes SET solde = solde + 500 WHERE id = 2;", cls: "" },
                { id: "rb-error", text: "⚠️ Erreur détectée — contrainte violée ou logique applicative", cls: "rollback" },
                { id: "rb-rollback", text: "ROLLBACK;", cls: "rollback" }
            ],
            steps: [
                {
                    key: "rb-begin",
                    sql: "BEGIN — ouverture de la transaction",
                    desc: "La transaction démarre. La base est encore dans son état initial.",
                    render: () => {
                        return bannerHtml("open", "🔓", "Transaction ouverte — aucune modification encore appliquée") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État initial de la table comptes", tableHtml(comptes, COLS, null))}
                            </div>`;
                    }
                },
                {
                    key: "rb-update1",
                    sql: "UPDATE — débit du compte d'Alice",
                    desc: "Alice perd 500. Modification temporaire en mémoire.",
                    render: () => {
                        const data = comptes.map(r => r.id === 1
                            ? { ...r, solde: r.solde - 500, _state: "modified" }
                            : { ...r, _state: "normal" }
                        );
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state === "modified" ? "modified" : ""; });
                        return bannerHtml("open", "⏳", "Transaction en cours — 1 modification temporaire") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("pending", "⏳ État en mémoire (non committé)", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#93c5fd", "Modifié temporairement"]])}`;
                    }
                },
                {
                    key: "rb-update2",
                    sql: "UPDATE — crédit du compte de Bob",
                    desc: "Bob reçoit 500. Modification temporaire en mémoire.",
                    render: () => {
                        const data = comptes.map(r =>
                            r.id === 1 ? { ...r, solde: r.solde - 500, _state: "modified" } :
                                r.id === 2 ? { ...r, solde: r.solde + 500, _state: "modified" } :
                                    { ...r, _state: "normal" }
                        );
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state === "modified" ? "modified" : ""; });
                        return bannerHtml("open", "⏳", "Transaction en cours — 2 modifications temporaires") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("pending", "⏳ État en mémoire (non committé)", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#93c5fd", "Modifié temporairement"]])}`;
                    }
                },
                {
                    key: "rb-error",
                    sql: "⚠️ Erreur détectée — contrainte violée ou logique applicative",
                    desc: "Une <strong>erreur</strong> est détectée (contrainte violée ou logique applicative). Le moteur décide de <strong>ne pas valider</strong> la transaction. Un ROLLBACK va être déclenché.",
                    render: () => {
                        const data = comptes.map(r =>
                            r.id === 1 ? { ...r, solde: r.solde - 500, _state: "deleted" } :
                                r.id === 2 ? { ...r, solde: r.solde + 500, _state: "deleted" } :
                                    { ...r, _state: "normal" }
                        );
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state === "deleted" ? "deleted" : ""; });
                        return bannerHtml("rollback", "⚠️", "Erreur détectée — les modifications vont être annulées") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("rollback", "⚠️ Modifications en attente (vont être annulées)", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#fca5a5", "Modification qui va être annulée"]])}`;
                    }
                },
                {
                    key: "rb-rollback",
                    sql: "ROLLBACK — annulation complète",
                    desc: "<strong>Toutes les modifications sont annulées</strong>. La base retrouve exactement son état avant le BEGIN. Aucune donnée n'a été modifiée.",
                    render: () => {
                        const stateMap = { 1: "rolledback", 2: "rolledback" };
                        return bannerHtml("rollback", "↩️", "ROLLBACK — la base est revenue à son état initial") +
                            `<div class="result-block danger">
                                ${blockHtml("rollback", "↩️ État après ROLLBACK — identique à l'état initial", tableHtml(comptes, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#f1f5f9", "Ligne revenue à son état d'origine"]])}`;
                    }
                }
            ]
        },

        // ── SAVEPOINT ─────────────────────────────────────
        savepoint: {
            parts: [
                { id: "sp-begin", text: "BEGIN;", cls: "commit" },
                { id: "sp-update1", text: "  UPDATE comptes SET solde = solde - 500 WHERE id = 1;", cls: "" },
                { id: "sp-save", text: "  SAVEPOINT avant_insert;", cls: "savepoint" },
                { id: "sp-insert", text: "  INSERT INTO comptes VALUES (5, 'Emma', 1000);", cls: "" },
                { id: "sp-error", text: "  -- ⚠️ Erreur sur l'INSERT !", cls: "rollback" },
                { id: "sp-rollback", text: "  ROLLBACK TO SAVEPOINT avant_insert;", cls: "rollback" },
                { id: "sp-commit", text: "COMMIT;", cls: "commit" }
            ],
            steps: [
                {
                    key: "sp-begin",
                    sql: "BEGIN — ouverture de la transaction",
                    desc: "La transaction démarre. La base est dans son état initial.",
                    render: () => {
                        return bannerHtml("open", "🔓", "Transaction ouverte") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État initial", tableHtml(comptes, COLS, null))}
                            </div>`;
                    }
                },
                {
                    key: "sp-update1",
                    sql: "UPDATE — débit d'Alice",
                    desc: "Alice perd 500. Modification temporaire en mémoire.",
                    render: () => {
                        const data = comptes.map(r => r.id === 1
                            ? { ...r, solde: r.solde - 500, _state: "modified" }
                            : { ...r, _state: "normal" }
                        );
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state === "modified" ? "modified" : ""; });
                        return bannerHtml("open", "⏳", "Transaction en cours") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("pending", "⏳ État en mémoire", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#93c5fd", "Modifié temporairement"]])}`;
                    }
                },
                {
                    key: "sp-save",
                    sql: "SAVEPOINT avant_insert — point de sauvegarde",
                    desc: "<strong>Un point de sauvegarde est posé</strong> ici. Si une erreur survient plus loin, on pourra revenir à cet état précis sans annuler toute la transaction.",
                    render: () => {
                        const data = comptes.map(r => r.id === 1
                            ? { ...r, solde: r.solde - 500, _state: "savepointed" }
                            : { ...r, _state: "normal" }
                        );
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state === "savepointed" ? "savepointed" : ""; });
                        return bannerHtml("savepoint", "📌", "SAVEPOINT posé — retour possible à cet état si erreur") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("savepoint", "📌 État au SAVEPOINT (mémorisé)", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#fed7aa", "État mémorisé au SAVEPOINT"]])}`;
                    }
                },
                {
                    key: "sp-insert",
                    sql: "INSERT — ajout du compte d'Emma",
                    desc: "Tentative d'insertion d'Emma. Cette opération est après le SAVEPOINT.",
                    render: () => {
                        const data = [
                            ...comptes.map(r => r.id === 1
                                ? { ...r, solde: r.solde - 500, _state: "savepointed" }
                                : { ...r, _state: "normal" }
                            ),
                            { id: 5, titulaire: "Emma", solde: 1000, _state: "inserted" }
                        ];
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state !== "normal" ? r._state : ""; });
                        return bannerHtml("open", "⏳", "Transaction en cours — INSERT après le SAVEPOINT") +
                            `<div class="tables-grid">
                                ${blockHtml("savepoint", "📌 État au SAVEPOINT", tableHtml(comptes.map(r => r.id === 1 ? { ...r, solde: r.solde - 500 } : r), COLS, null))}
                                ${blockHtml("pending", "⏳ État en mémoire avec INSERT", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#fed7aa", "État SAVEPOINT"], ["#bbf7d0", "Inséré après SAVEPOINT"]])}`;
                    }
                },
                {
                    key: "sp-error",
                    sql: "⚠️ Erreur sur l'INSERT",
                    desc: "L'INSERT échoue (contrainte violée, doublon…). Grâce au SAVEPOINT, on peut annuler <strong>uniquement l'INSERT</strong> et conserver le UPDATE précédent.",
                    render: () => {
                        const data = [
                            ...comptes.map(r => r.id === 1
                                ? { ...r, solde: r.solde - 500, _state: "savepointed" }
                                : { ...r, _state: "normal" }
                            ),
                            { id: 5, titulaire: "Emma", solde: 1000, _state: "deleted" }
                        ];
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state !== "normal" ? r._state : ""; });
                        return bannerHtml("rollback", "⚠️", "Erreur sur l'INSERT — ROLLBACK TO SAVEPOINT va être déclenché") +
                            `<div class="tables-grid">
                                ${blockHtml("savepoint", "📌 État au SAVEPOINT (va être restauré)", tableHtml(comptes.map(r => r.id === 1 ? { ...r, solde: r.solde - 500 } : r), COLS, null))}
                                ${blockHtml("rollback", "⚠️ Opérations après SAVEPOINT (vont être annulées)", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#fca5a5", "Va être annulé"], ["#fed7aa", "Conservé (avant SAVEPOINT)"]])}`;
                    }
                },
                {
                    key: "sp-rollback",
                    sql: "ROLLBACK TO SAVEPOINT avant_insert",
                    desc: "Seules les opérations <strong>après le SAVEPOINT</strong> sont annulées. Le UPDATE d'Alice est conservé. La transaction reste ouverte.",
                    render: () => {
                        const data = comptes.map(r => r.id === 1
                            ? { ...r, solde: r.solde - 500, _state: "savepointed" }
                            : { ...r, _state: "normal" }
                        );
                        const stateMap = {};
                        data.forEach(r => { stateMap[r.id] = r._state === "savepointed" ? "savepointed" : ""; });
                        return bannerHtml("savepoint", "↩️", "ROLLBACK TO SAVEPOINT — retour au point de sauvegarde, transaction encore ouverte") +
                            `<div class="tables-grid">
                                ${blockHtml("before", "📋 État avant transaction", tableHtml(comptes, COLS, null))}
                                ${blockHtml("savepoint", "📌 État restauré au SAVEPOINT", tableHtml(data, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#fed7aa", "UPDATE conservé, INSERT annulé"]])}`;
                    }
                },
                {
                    key: "sp-commit",
                    sql: "COMMIT — validation du reste de la transaction",
                    desc: "Seul le UPDATE d'Alice (avant le SAVEPOINT) est validé définitivement. L'INSERT d'Emma a bien été annulé.",
                    render: () => {
                        const final = comptes.map(r => r.id === 1
                            ? { ...r, solde: r.solde - 500 }
                            : { ...r }
                        );
                        const stateMap = { 1: "committed" };
                        return bannerHtml("commit", "✅", "COMMIT — seul le UPDATE avant le SAVEPOINT est persisté") +
                            `<div class="result-block">
                                ${blockHtml("commit", "✅ État final après COMMIT", tableHtml(final, COLS, stateMap))}
                            </div>
                            ${legendHtml([["#86efac", "UPDATE validé et persisté"], ["#fff", "Inchangé"]])}`;
                    }
                }
            ]
        }
    };

    // ── État global ────────────────────────────────────────
    const tabConfig = {
        commit: { cls: "active-commit", query: queries.commit },
        rollback: { cls: "active-rollback", query: queries.rollback },
        savepoint: { cls: "active-savepoint", query: queries.savepoint }
    };

    let activeQuery = queries.commit;
    let curStepIdx = -1;

    // ── Rendu SQL ──────────────────────────────────────────
    function renderSql() {
        document.getElementById("sql-display").innerHTML =
            activeQuery.parts.map(p =>
                `<span class="sql-part ${p.cls || ""}" id="${p.id}">${p.text}</span>`
            ).join("\n");
    }

    // ── Mise à jour UI ─────────────────────────────────────
    function updateStep(i) {
        curStepIdx = i;
        const step = activeQuery.steps[i];

        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        const el = document.getElementById(step.key);
        if (el) el.classList.add("active");

        document.getElementById("step-meta").innerHTML =
            `<strong>Étape ${i + 1} / ${activeQuery.steps.length} — ${step.sql}</strong><br>${step.desc}`;

        document.getElementById("step-output").innerHTML = step.render();
    }

    // ── Reset ──────────────────────────────────────────────
    function reset() {
        curStepIdx = -1;
        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        document.getElementById("step-meta").textContent =
            "Cliquez sur un bouton pour avancer ou reculer dans l'exécution.";
        document.getElementById("step-output").innerHTML = "";
    }

    // ── Navigation ─────────────────────────────────────────
    document.getElementById("btn-next").addEventListener("click", () => {
        if (curStepIdx < activeQuery.steps.length - 1) updateStep(curStepIdx + 1);
    });

    document.getElementById("btn-prev").addEventListener("click", () => {
        if (curStepIdx > 0) updateStep(curStepIdx - 1);
    });

    document.getElementById("btn-reset").addEventListener("click", reset);

    // ── Bascule onglets ────────────────────────────────────
    Object.keys(tabConfig).forEach(key => {
        document.getElementById(`tab-${key}`).addEventListener("click", () => {
            activeQuery = tabConfig[key].query;
            Object.keys(tabConfig).forEach(k => {
                document.getElementById(`tab-${k}`).className =
                    k === key ? `query-tab ${tabConfig[k].cls}` : "query-tab";
            });
            reset();
            renderSql();
        });
    });

    // ── Init ───────────────────────────────────────────────
    renderSql();
})();